// Shared colour and geometry helpers for booking-page themes.
//
// These live here so the public profile theme builder and the professional
// visual editor resolve colours identically instead of keeping private copies.

export const DEFAULT_LIGHT_TEXT = "#ffffff";
export const DEFAULT_DARK_TEXT = "#111111";
export const DEFAULT_TEXT_COLOR = "#0f172a";
export const DEFAULT_BORDER_RADIUS = 24;

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const normalizeEnum = (value, allowedValues, fallbackValue) => {
  if (typeof value !== "string") {
    return fallbackValue;
  }
  const normalized = value.trim().toLowerCase();
  return allowedValues.includes(normalized) ? normalized : fallbackValue;
};

export const isHexColor = (value) => /^#(?:[0-9a-fA-F]{6})$/.test(value || "");

export const hexToRgb = (hexValue) => {
  const normalized = String(hexValue).replace("#", "");
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

export const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0"))
    .join("")}`;

export const mixHexColors = (colorA, colorB, weight = 0.5) => {
  if (!isHexColor(colorA) || !isHexColor(colorB)) {
    return colorA;
  }
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);
  const ratio = clamp(weight, 0, 1);

  return rgbToHex({
    r: rgbA.r + (rgbB.r - rgbA.r) * ratio,
    g: rgbA.g + (rgbB.g - rgbA.g) * ratio,
    b: rgbA.b + (rgbB.b - rgbA.b) * ratio,
  });
};

export const hexToRgba = (hexValue, alpha) => {
  if (!isHexColor(hexValue)) {
    return `rgba(15, 23, 42, ${alpha})`;
  }
  const { r, g, b } = hexToRgb(hexValue);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const relativeLuminance = (hexValue) => {
  const { r, g, b } = hexToRgb(hexValue);
  const normalizeChannel = (value) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  return (
    0.2126 * normalizeChannel(r) +
    0.7152 * normalizeChannel(g) +
    0.0722 * normalizeChannel(b)
  );
};

export const contrastRatio = (colorA, colorB) => {
  if (!isHexColor(colorA) || !isHexColor(colorB)) {
    return 1;
  }
  const luminanceA = relativeLuminance(colorA);
  const luminanceB = relativeLuminance(colorB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
};

export const findContrastCandidate = (
  backgroundColor,
  preferredColor,
  targetColor,
  minimumContrast = 4.5
) => {
  if (!isHexColor(backgroundColor) || !isHexColor(preferredColor) || !isHexColor(targetColor)) {
    return null;
  }

  for (let step = 1; step <= 20; step += 1) {
    const weight = step / 20;
    const candidate = mixHexColors(preferredColor, targetColor, weight);
    if (contrastRatio(backgroundColor, candidate) >= minimumContrast) {
      return { candidate, weight };
    }
  }

  return null;
};

export const resolveReadableColor = (
  backgroundColor,
  preferredColor,
  fallbackCandidates = [],
  minimumContrast = 4.5
) => {
  if (!isHexColor(backgroundColor)) {
    return isHexColor(preferredColor)
      ? preferredColor
      : fallbackCandidates.find(isHexColor) || DEFAULT_TEXT_COLOR;
  }

  if (isHexColor(preferredColor)) {
    const preferredContrast = contrastRatio(backgroundColor, preferredColor);
    if (preferredContrast >= minimumContrast) {
      return preferredColor;
    }

    const towardDark = findContrastCandidate(
      backgroundColor,
      preferredColor,
      DEFAULT_DARK_TEXT,
      minimumContrast
    );
    const towardLight = findContrastCandidate(
      backgroundColor,
      preferredColor,
      DEFAULT_LIGHT_TEXT,
      minimumContrast
    );
    const adjustedCandidates = [towardDark, towardLight]
      .filter(Boolean)
      .sort((a, b) => a.weight - b.weight);

    if (adjustedCandidates.length > 0) {
      return adjustedCandidates[0].candidate;
    }
  }

  const fallbackColors = fallbackCandidates.filter(isHexColor);
  if (fallbackColors.length === 0) {
    return DEFAULT_TEXT_COLOR;
  }

  let bestFallback = fallbackColors[0];
  let bestContrast = contrastRatio(backgroundColor, bestFallback);

  fallbackColors.forEach((candidate) => {
    const ratio = contrastRatio(backgroundColor, candidate);
    if (ratio > bestContrast) {
      bestFallback = candidate;
      bestContrast = ratio;
    }
  });

  return bestFallback;
};

export const resolveShadowValue = (shadowStyle) => {
  if (shadowStyle === "none") {
    return "none";
  }
  if (shadowStyle === "lifted") {
    return "0 18px 40px rgba(15, 23, 42, 0.14)";
  }
  return "0 10px 30px rgba(15, 23, 42, 0.08)";
};

export const resolveBorderRadius = (value) => {
  const parsed = toNumber(value);
  if (parsed === null) {
    return DEFAULT_BORDER_RADIUS;
  }
  return clamp(Math.floor(parsed), 8, 32);
};
